import React, { useEffect } from 'react';

const RequestDemoPage = () => {
  useEffect(() => {
    const scriptId = 'deftform-embed-script';
    if (document.getElementById(scriptId)) return;

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://cdn.deftform.com/embed.js';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  const expectations = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
          <path fillRule="evenodd"
                d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                clipRule="evenodd"/>
        </svg>
      ),
      title: "Deep-dive into Vermillion's capabilities",
      description: "See how our platform transforms document-heavy operations with intelligent automation."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd"
                d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z"
                clipRule="evenodd"/>
        </svg>
      ),
      title: "Live walkthrough of our ecosystem",
      description: "Interactive demonstration of the platform with your specific workflows in mind."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"/>
        </svg>
      ),
      title: "A path to resolve your challenges",
      description: "Clear implementation roadmap aligned with your business objectives."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
          <path fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
                clipRule="evenodd"/>
        </svg>
      ),
      title: "Personalized ROI analysis",
      description: "Calculate potential efficiency gains and cost savings specific to your organization."
    }
  ];

  return (
    <section className="pt-24 pb-16 sm:pt-32 sm:pb-24 bg-surface-secondary">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl overflow-hidden shadow-lg flex flex-col lg:flex-row">

          {/* Left: dark branded panel */}
          <div className="w-full lg:w-1/2 bg-surface-dark p-10 sm:p-14 flex flex-col justify-between">
            <div>
              <div className="inline-block bg-vermillion-600 text-white text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-6">
                Request a Demo
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
                See Vermillion<br />in Action
              </h1>
              <p className="text-gray-400 text-base leading-relaxed mb-10">
                Book your personalized demo and take the first step towards faster, more accurate, and smarter decisions.
              </p>

              <div className="space-y-7">
                {expectations.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-0.5 text-vermillion-400">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{item.title}</p>
                      <p className="text-gray-400 text-sm mt-0.5 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom trust line */}
            <p className="text-gray-600 text-xs mt-12">
              Trusted by operations teams across industries.
            </p>
          </div>

          {/* Right: form panel */}
          <div className="w-full lg:w-1/2 bg-white p-10 sm:p-14 flex flex-col justify-center">
            <h2 className="text-xl font-bold text-gray-900 mb-1">Book your demo</h2>
            <p className="text-gray-400 text-sm mb-8">We'll be in touch within one business day.</p>

            <div
              id="demoForm"
              className="deftform"
              data-form-id="60e04cfb-cc11-4373-9f6f-74d7dbd1ba3b"
              data-form-width="100%"
              data-form-align="center"
              data-form-auto-height="1"
            ></div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default RequestDemoPage;
